import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay } from "date-fns";

interface MetricsFilters {
  productId?: string;
  startDate: Date;
  endDate: Date;
}

export interface MetricBreakdown {
  product_name: string | null;
  period_start: string;
  period_end: string;
  customer_count?: number;
  transaction_count?: number;
  value: number;
  source?: string;
  churn_rate?: number;
  total_customers?: number;
}

export interface MetricDetailsData {
  breakdown: MetricBreakdown[];
  total: number;
  average?: number;
}

export const useMetricsDetails = (
  metricType: 'mrr' | 'arr' | 'churn' | 'revenue' | 'cac' | 'ltv' | 'average_ticket',
  filters: MetricsFilters
) => {
  return useQuery({
    queryKey: ["metrics-details", metricType, filters.productId, filters.startDate, filters.endDate],
    queryFn: async (): Promise<MetricDetailsData> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: userRole } = await supabase
        .from("user_roles")
        .select("tenant_id")
        .eq("user_id", user.id)
        .single();

      if (!userRole) throw new Error("No tenant found");

      const startDateStr = startOfDay(filters.startDate).toISOString();
      const endDateStr = endOfDay(filters.endDate).toISOString();

      // Query base para métricas
      let query = supabase
        .from("metrics")
        .select(`
          *,
          products:product_id (name),
          integrations:integration_id (provider)
        `)
        .eq("tenant_id", userRole.tenant_id)
        .gte("period_start", startDateStr)
        .lte("period_end", endDateStr);

      if (filters.productId) {
        query = query.eq("product_id", filters.productId);
      }

      // Filtrar por tipo de métrica
      const actualMetricType = metricType === 'arr' ? 'mrr' : metricType === 'average_ticket' ? 'revenue' : metricType;
      query = query.eq("metric_type", actualMetricType);

      const { data: metrics, error } = await query;
      if (error) throw error;

      // Processar dados por tipo de métrica
      const breakdown: MetricBreakdown[] = [];
      let total = 0;

      if (metricType === 'mrr' || metricType === 'arr') {
        // Agrupar por produto
        const grouped = metrics?.reduce((acc: any, m: any) => {
          const key = m.product_id || 'sem-produto';
          if (!acc[key]) {
            acc[key] = {
              product_name: m.products?.name || 'Sem Produto',
              period_start: m.period_start,
              period_end: m.period_end,
              value: 0,
              customer_ids: new Set()
            };
          }
          acc[key].value += Number(m.value);
          if (m.customer_id) acc[key].customer_ids.add(m.customer_id);
          return acc;
        }, {});

        Object.values(grouped || {}).forEach((item: any) => {
          const value = metricType === 'arr' ? item.value * 12 : item.value;
          breakdown.push({
            product_name: item.product_name,
            period_start: item.period_start,
            period_end: item.period_end,
            customer_count: item.customer_ids.size,
            value
          });
          total += value;
        });
      } else if (metricType === 'churn') {
        // Calcular churn por produto
        const churnByProduct = metrics?.reduce((acc: any, m: any) => {
          const key = m.product_id || 'sem-produto';
          if (!acc[key]) {
            acc[key] = {
              product_name: m.products?.name || 'Sem Produto',
              period_start: m.period_start,
              period_end: m.period_end,
              churned: 0,
              total: 0
            };
          }
          acc[key].churned += Number(m.value);
          return acc;
        }, {});

        // Buscar total de clientes por produto
        const productIds = [...new Set(metrics?.map(m => m.product_id).filter(Boolean))];
        
        for (const productId of productIds) {
          let customerQuery = supabase
            .from("customers")
            .select("id", { count: "exact" })
            .eq("tenant_id", userRole.tenant_id);

          if (productId) {
            customerQuery = customerQuery.eq("product_id", productId);
          }

          const { count } = await customerQuery;
          const key = productId || 'sem-produto';
          if (churnByProduct[key]) {
            churnByProduct[key].total = count || 0;
          }
        }

        let totalChurned = 0;
        let totalCustomers = 0;

        Object.values(churnByProduct || {}).forEach((item: any) => {
          const churnRate = item.total > 0 ? (item.churned / item.total) * 100 : 0;
          breakdown.push({
            product_name: item.product_name,
            period_start: item.period_start,
            period_end: item.period_end,
            customer_count: item.churned,
            total_customers: item.total,
            churn_rate: churnRate,
            value: churnRate
          });
          totalChurned += item.churned;
          totalCustomers += item.total;
        });

        total = totalCustomers > 0 ? (totalChurned / totalCustomers) * 100 : 0;
      } else if (metricType === 'revenue' || metricType === 'average_ticket') {
        // Agrupar por integração/fonte
        const grouped = metrics?.reduce((acc: any, m: any) => {
          const source = m.integrations?.provider || 'Manual';
          if (!acc[source]) {
            acc[source] = {
              source,
              period_start: m.period_start,
              period_end: m.period_end,
              value: 0,
              count: 0
            };
          }
          acc[source].value += Number(m.value);
          acc[source].count += 1;
          return acc;
        }, {});

        Object.values(grouped || {}).forEach((item: any) => {
          breakdown.push({
            product_name: null,
            period_start: item.period_start,
            period_end: item.period_end,
            source: item.source,
            transaction_count: item.count,
            value: item.value
          });
          total += item.value;
        });
      } else {
        // CAC, LTV - calcular média
        metrics?.forEach((m: any) => {
          breakdown.push({
            product_name: m.products?.name || 'Sem Produto',
            period_start: m.period_start,
            period_end: m.period_end,
            value: Number(m.value)
          });
          total += Number(m.value);
        });
      }

      const average = breakdown.length > 0 ? total / breakdown.length : 0;

      return {
        breakdown: breakdown.sort((a, b) => b.value - a.value),
        total,
        average
      };
    },
  });
};
